import { useRef, useState } from "react";
import { Spinner } from "../../../../common/spinner";
import { useSubscriptionServices } from "../finance_services";
import { Pagination } from "../../../../common/pagination";
import { GENERAL_PAGINATION_SIZE } from "../../../../../lib/common/constants";
import { SubscriptionRecordList } from "./subscription_records";
import { TEnabledDisabledStates } from "../../../../../services/backoffice/backoffice-service";
import { SubscriptionFilterPanel, TSubscriptionFilterSettings } from "./filter-panel";

export const SubscriptionAdmin = () => {
    const { subscriptionList, workingState, totalRows, currentPage, setCurrentPage, getAllSubscriptions, editCredits } = useSubscriptionServices();

    const subscriptionsListRef = useRef<HTMLDivElement>(null);
    const [chevronUp, setChevronUp] = useState(true);
    const enableFilter = useRef<TEnabledDisabledStates>("all");
    const nameFilter = useRef<string>("");
    const isFilterSearching = useRef<boolean>(false);

    const applyFilters = async (nameToSearch: string) => {
        if (isFilterSearching.current) return;

        isFilterSearching.current = true;
        await getAllSubscriptions(0, {
            name: nameToSearch || "",
            enabled: enableFilter.current,
        });
        isFilterSearching.current = false;

        if (nameToSearch !== nameFilter.current) applyFilters(nameFilter.current);
    };

    const filterCallback = (filterSettings: TSubscriptionFilterSettings) => {
        const { enabled, name } = filterSettings;
        nameFilter.current = name || "";
        enableFilter.current = enabled || "all";

        applyFilters(nameFilter.current);
    };

    const resetFilters = () => {
        nameFilter.current = "";
        enableFilter.current = "all";

        applyFilters(nameFilter.current);
    };

    const editCreditHandler = async (userid: number, credits: number) => {
        await editCredits(userid, credits);
        await getAllSubscriptions(0, {
            enabled: enableFilter.current,
            name: "",
        });
    };

    return (
        <div className="row admin-section p-3 m-auto mb-4" ref={subscriptionsListRef}>
            <div className="pb-2">
                <div className="hstack">
                    <h4 className="text-secondary fw-bold">Subscriptions</h4>
                    <button
                        className="btn btn-outline-primary btn-sm ms-auto px-3"
                        type="button"
                        data-bs-toggle="collapse"
                        data-bs-target="#collapseSubscription"
                        aria-expanded="true"
                        aria-controls="collapseSubscription"
                        onClick={() => {
                            setChevronUp((prev) => !prev);
                        }}
                    >
                        {chevronUp ? "Hide" : "Show"}
                        <i className={`fa-solid ms-2 ${chevronUp ? "fa-chevron-up" : "fa-chevron-down"}`}></i>
                    </button>
                </div>
                <p>
                    <small>
                        Subscription records for all registered dental employers, including account credits, payment setup, update history, and administrative
                        block status.
                    </small>
                    <br />
                    <small>
                        <strong>1 Credit = 1 Free Month</strong>
                    </small>
                </p>
            </div>
            <div className="collapse show" id="collapseSubscription">
                <div>
                    <SubscriptionFilterPanel filterCallback={filterCallback} resetCallback={resetFilters} />
                    {workingState === "working" && (
                        <div className="p-4 text-primary">
                            <Spinner spinnerColor="text-primary" />
                        </div>
                    )}
                    {workingState === "done" && subscriptionList.length > 0 && (
                        <>
                            <SubscriptionRecordList subscriptionList={subscriptionList} editCreditCallback={editCreditHandler} />

                            {totalRows > GENERAL_PAGINATION_SIZE && (
                                <div className="pb-3">
                                    <Pagination
                                        currentPage={currentPage}
                                        totalCount={totalRows}
                                        itemsPerPage={GENERAL_PAGINATION_SIZE}
                                        refObject={subscriptionsListRef}
                                        onPageChange={(offset) => {
                                            getAllSubscriptions(offset, {
                                                enabled: "all",
                                                name: "",
                                            });
                                            setCurrentPage(offset + 1);
                                        }}
                                    />
                                </div>
                            )}
                        </>
                    )}
                    {workingState === "done" && subscriptionList.length === 0 && <p className="text-danger">No Subscriptions Found.</p>}
                </div>
            </div>
        </div>
    );
};
